import { useCallback, useEffect, useId, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { formatUtcOffsetLabel } from '@/lib/timezoneOffsetLabel';
import { cn } from '@/lib/utils';

export type HeatmapGridCellProps = {
  dayLabel: string;
  /** Displayed time window, e.g. "12 AM – 2 AM (UTC+10)". */
  timeLabel: string;
  value: number;
  /** Metric noun after the count, e.g. "events". */
  valueNoun?: string;
  /** Optional secondary line, e.g. share of peak. */
  detail?: string;
  className?: string;
  style?: CSSProperties;
};

type TipPosition = { x: number; y: number };

/**
 * Colored heatmap cell with an immediate floating hover tooltip
 * (replaces delayed native `title` tooltips; portals so overflow parents don't clip).
 */
export function HeatmapGridCell({
  dayLabel,
  timeLabel,
  value,
  valueNoun = 'events',
  detail,
  className,
  style,
}: HeatmapGridCellProps) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TipPosition>({ x: 0, y: 0 });

  const place = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const countLabel = `${value.toLocaleString()} ${valueNoun}`;
  const ariaLabel = `${dayLabel}, ${timeLabel}, ${countLabel}`;

  return (
    <>
      <div
        role="img"
        aria-label={ariaLabel}
        aria-describedby={open ? tipId : undefined}
        className={cn('cursor-default outline-none', className)}
        style={style}
        onMouseEnter={(e) => {
          place(e.currentTarget);
          setOpen(true);
        }}
        onMouseMove={(e) => place(e.currentTarget)}
        onMouseLeave={() => setOpen(false)}
        onFocus={(e) => {
          place(e.currentTarget);
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        tabIndex={0}
      />
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tipId}
              role="tooltip"
              className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-[calc(100%+8px)] rounded-md px-3 py-2 text-left shadow-lg"
              style={{
                left: pos.x,
                top: pos.y,
                backgroundColor: '#1e2a44',
                maxWidth: 280,
              }}
            >
              <div className="whitespace-normal text-[11px] font-medium text-white/70">
                {dayLabel} · {timeLabel}
              </div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-white">{countLabel}</div>
              {detail ? (
                <div className="mt-1 text-[11px] text-white/55">{detail}</div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * Formats a 2-hour slot as "12 AM – 2 AM".
 * When `timeZone` is set, appends the zone offset, e.g. "12 AM – 2 AM (UTC+10)".
 */
export function formatHeatmapSlotRange(startHour: number, timeZone?: string): string {
  const endHour = (startHour + 2) % 24;
  const range = `${formatHourLabel(startHour)} – ${formatHourLabel(endHour)}`;
  if (!timeZone) return range;
  return `${range} (${formatUtcOffsetLabel(timeZone)})`;
}

function formatHourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}
